import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useThailandJobEmployersList } from "../thailandJobs/useThailandJobEmployersList.js";
import { listEmployerApplications } from "./employerApplicationsApi.js";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
// Matches AgentWorkerPanel's own page size and debounce so the two sides of
// the Org Chart behave identically - the only difference between them should
// be WHAT the line is made of, never how the screen responds.
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

function describeRosterError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถโหลดรายชื่อนายจ้างได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * P156. The job-side twin of AgentWorkerPanel.
 *
 * The owner, on seeing a two-number summary here: "ของนายจ้างแสดงให้เหมือน
 * แรงงานสิครับ ทำไมแสดงเป็นจำนวนล่ะ คุณก็เอานายจ้างมาต่อกับ agent ไงครับ".
 * Right - the worker side lists the Agent's actual people underneath the
 * chart, so the job side has to list the Agent's actual employers, not count
 * them. A count tells an operator nothing they can act on.
 *
 * Reads the same real GET /api/backoffice/thailand-jobs/employers endpoint the
 * SuperAdmin Employers page uses. No agentId is sent: EmployerService resolves
 * visibility from the session, so an Agent already receives exactly its own
 * employers (verified live signed in as this Agent - 2 employers, both his).
 * Sending an explicit filter would only invite the two to disagree.
 */
/**
 * P156. The pending half of the roster: companies that have submitted the
 * Agent's Employer Invite Card form but have not been turned into an employer
 * yet. Kept in its own small hook rather than folded into the employers list -
 * they are two different backend record types and merging them at the data
 * layer is how a pending application quietly starts being counted as a real
 * employer.
 */
function useEmployerApplications(agentId, search) {
  const [state, setState] = useState({ status: "loading", items: [], totalCount: 0 });

  useEffect(() => {
    if (!agentId) {
      return undefined;
    }

    let cancelled = false;
    listEmployerApplications(agentId, { pageSize: PAGE_SIZE, search: search || undefined })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", items: data.items ?? [], totalCount: data.totalCount ?? 0 });
        }
      })
      .catch(() => {
        // A failure here must not blank the employers list beside it - the
        // pending section simply does not render.
        if (!cancelled) {
          setState({ status: "error", items: [], totalCount: 0 });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, search]);

  return state;
}

export default function AgentEmployerPanel({ agentId }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Memoised so the hook's effect does not re-fire on every render with a
  // fresh object identity - the same trap AgentWorkerPanel's caller avoids.
  const filters = useMemo(() => ({ page, pageSize: PAGE_SIZE, search: search || undefined }), [page, search]);
  // No enabled flag: this panel is only mounted for a job-side Agent, and
  // useThailandJobEmployersList has no null-filter path to fall into.
  const roster = useThailandJobEmployersList(filters);
  const applications = useEmployerApplications(agentId, search);

  const items = roster.data?.items ?? [];
  const totalCount = roster.data?.totalCount ?? null;
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;
  const isInitialLoad = roster.status === "loading" && items.length === 0;

  return (
    <section className="wx-panel wx-aoc-worker-panel">
      <div className="wx-aoc-worker-head">
        <div>
          <h2 className="wx-panel-title">นายจ้างในสายงาน Agent นี้</h2>
          <span className="wx-aoc-worker-total">
            {totalCount === null ? "กำลังโหลด..." : `นายจ้างในสายงานนี้: ${totalCount.toLocaleString("th-TH")} ราย`}
          </span>
        </div>
      </div>

      <div className="wx-aoc-worker-toolbar">
        <label className="wx-aoc-search">
          <span className="wx-visually-hidden">ค้นหานายจ้าง</span>
          <Search size={14} aria-hidden="true" className="wx-aoc-search-icon" />
          <input
            type="search"
            placeholder="ค้นหาชื่อนายจ้าง หรือรหัส..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      {isInitialLoad ? (
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลดรายชื่อนายจ้าง..." />
      ) : roster.status === "error" ? (
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <p className="wx-state-body">{describeRosterError(roster.error)}</p>
        </div>
      ) : items.length === 0 ? (
        <p className="wx-empty-list">{search ? "ไม่พบนายจ้างที่ตรงกับคำค้นหา" : "ยังไม่มีนายจ้างในสายงานนี้"}</p>
      ) : (
        <>
          <div className="wx-aoc-worker-cards">
            {items.map((employer) => (
              <EmployerOrgRow key={employer.employerId} employer={employer} />
            ))}
          </div>

          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
        </>
      )}

      {applications.items.length > 0 && (
        <div className="wx-aoc-emp-pending">
          <h3 className="wx-aoc-emp-pending-title">
            รอตรวจสอบ <span className="wx-badge wx-badge--info">{applications.totalCount.toLocaleString("th-TH")}</span>
          </h3>
          <p className="wx-aoc-worker-total">นายจ้างที่กรอกแบบฟอร์มจากลิงก์คำเชิญของคุณแล้ว แต่ยังไม่ได้เพิ่มเข้าระบบ</p>
          <div className="wx-aoc-worker-cards">
            {applications.items.map((application) => (
              <PendingEmployerRow key={application.applicationId} application={application} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * One company that submitted the form and is waiting. Deliberately NOT styled
 * as an employer row: it is not one yet, and an operator must be able to tell
 * the two apart at a glance.
 */
function PendingEmployerRow({ application }) {
  const name = application.displayName || application.legalName;
  const needsParts = [];
  if (application.desiredWorkerType) {
    needsParts.push(application.desiredWorkerType);
  }
  if (application.desiredWorkerCount) {
    needsParts.push(`${application.desiredWorkerCount.toLocaleString("th-TH")} คน`);
  }
  if (application.desiredNationalities) {
    needsParts.push(application.desiredNationalities.split(",").join(", "));
  }

  return (
    <div className="wx-aoc-emp-row wx-aoc-emp-row--pending">
      <div className="wx-aoc-emp-row-main">
        <span className="wx-aoc-emp-name">{name}</span>
        <span className="wx-aoc-emp-code">
          {application.primaryContactName ? `${application.primaryContactName} · ` : ""}
          {application.primaryPhoneNumber}
        </span>
        {needsParts.length > 0 && <span className="wx-aoc-emp-needs">ต้องการ: {needsParts.join(" · ")}</span>}
      </div>
      <span className="wx-badge wx-badge--info">รอตรวจสอบ</span>
    </div>
  );
}

/**
 * One employer, hanging off this Agent. Shows only fields the list endpoint
 * genuinely returns - displayName can be null on a real row, so the code is
 * the fallback rather than an invented placeholder name.
 */
function EmployerOrgRow({ employer }) {
  const name = employer.displayName || employer.legalName || employer.employerCode;

  return (
    <Link to="/backoffice/thailand-jobs/employers" className="wx-aoc-emp-row">
      <div className="wx-aoc-emp-row-main">
        <span className="wx-aoc-emp-name">{name}</span>
        <span className="wx-aoc-emp-code">{employer.employerCode}</span>
      </div>
      <div className="wx-aoc-emp-row-stats">
        <span>
          งานเปิดรับ <strong>{employer.openJobCount ?? 0}</strong>
        </span>
        <span className="wx-aoc-inline-dot" aria-hidden="true" />
        <span>
        </span>
      </div>
      <span className={`wx-badge wx-badge--${employer.employerStatus === "Active" ? "success" : "neutral"}`}>
        {employer.employerStatus}
      </span>
    </Link>
  );
}
